import React from "react";
import { Link } from "react-router-dom";
import { Box, Flex } from "../../components/Flex";
import { Content } from "../../components/Layout";
import { Paragraph, Title } from "../../components/Text";
import { Button, TextButton } from "../../components/Button";
import { CloseIcon } from "../../icons/CloseIcon";
import { VerifiedIcon } from "../../icons/VerifiedIcon";
import { UntestedIcon } from "../../icons/UntestedIcon";
import { UnknownIcon } from "../../icons/UnknownIcon";
import { RevokeIcon } from "../../icons/RevokeIcon";
import { DoneIcon } from "../../icons/DoneIcon";
import { RemoveIcon } from "../../icons/RemoveIcon";
import { EditIcon } from "../../icons/EditIcon";
import { EditDetailsIcon } from "../../icons/EditDetailsIcon";
import { DownloadIcon } from "../../icons/DownloadIcon";
import { fixPackageSize, numberWithSpaces } from "../../utils";
import { Scrollbar } from "../../components/Scrollbar";
import { find, size, toLower } from "lodash";
import { KITSTYLE, SWTYPES } from "../../settings";
import { UploadProgress } from "./UploadProgress";


const PkgDetails = ({
    title,
    data,
    loading,
    verifyPackage,
    removePackage,
    editInfo,
    downloadPackage,
    uploadProgress,
    isContractsWrite,
    isPackageWrite,
    renderFieldContent,
    onClose
}) => {

    const fields = {
        info: [
            { label: "Version", attr: "version" },
            { label: "System Architecture", attr: "sysarch" },
            { label: "OS Version", attr: "minos" },
            { label: "Kit Style", attr: "style", render: (d) => find(KITSTYLE, { key: d.toLowerCase() }) ? find(KITSTYLE, { key: d.toLowerCase() }).title  : null },
            { label: "Kit Type", attr: "swtype", render: (d) => find(SWTYPES, { key: d.toLowerCase() }) ? find(SWTYPES, { key: d.toLowerCase() }).title  : null },
            { label: "ECO Kit Installation Rating", attr: "ecoRating" },
            { label: "Restart required", attr: "restart", render: d => d == 1 ? "Yes" : "No" },
            { label: "Registered Date", attr: "registeredDatetime" },
            { label: "Release Date", attr: "verifiedDate" },
            { label: "Registered By", attr: "registeredBy" },
            { label: "Verified By", attr: "verifiedBy" },
            { label: "Description", attr: "description" },
        ],
        file: [
            { label: "File Size", attr: "size", render: d => `${fixPackageSize(d)} (${numberWithSpaces(d)} bytes)` },
            { label: "MD5 Checksum", attr: "md5" },
        ]
    };


    return(
        <Content background="#FFFFFF" gap={{ bottom: 18 }}>
            <Flex width="100%" justify="space-between" margin={{ bottom: "24px"}}>
                <Flex align="center" gap={{ right: 8 }}>
                    {data.verified == 1 ? <VerifiedIcon /> : data.verified == 0 ? <UntestedIcon /> : <UnknownIcon />}
                    <Title h4 isLoading={loading}>
                        {title}
                    </Title>
                </Flex>
                <CloseIcon onClick={onClose} />
            </Flex>
            <Flex height="calc(100% - 108px)" justify="space-between" gap={{ right: 18 }}>
                <Scrollbar>
                    <Flex direction="column" gap={{ bottom: 8 }}>
                        {renderFieldContent(fields.info, data)}
                    </Flex>
                </Scrollbar>
                <Flex flex="0 0 450px">
                    <Scrollbar>
                        <Flex direction="column" gap={{ bottom: 19 }} padding={{ right: "19px" }}>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }} style={{ position: "relative" }}>
                                <Title h6 capitalize>Package Files</Title>
                                    {Array.isArray(data.files)
                                        ? <Flex direction="column" gap={{ bottom: 16 }}>
                                            {data.files.map(file => (
                                                uploadProgress[file.key]
                                                    ? <UploadProgress
                                                        key={file.key}
                                                        name={file.name}
                                                        progress={Math.min(uploadProgress[file.key].progress, uploadProgress[file.key].md5progress)}
                                                        status={uploadProgress[file.key].status}
                                                    />
                                                    : <Flex direction="column" align="flex-start" gap={{ right: 8, bottom: 8 }} key={file.key}>
                                                        <TextButton
                                                            maxWidth="390px"
                                                            style={{ fontWeight: 700 }}
                                                            onClick={() => downloadPackage({ id: file.key, minos: data.minos })}
                                                        >
                                                            &mdash; {file.name}
                                                        </TextButton>
                                                        {renderFieldContent(fields.file, file)}
                                                    </Flex>
                                            ))}
                                        </Flex>
                                        : null}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }}>
                                <Flex justify="space-between" align="center">
                                    <Title h6 capitalize>package restrictions</Title>
                                    {isContractsWrite && <TextButton  onClick={() => !loading && editInfo("restrictions", { key: data.key, contracts: data.contracts })}>
                                        <span><EditDetailsIcon /></span>
                                        <span>Edit</span>
                                    </TextButton>}
                                </Flex>
                                {Array.isArray(data.contracts)
                                    ? data.contracts.map((contract, idx) => (
                                        <Paragraph key={`pkg-contract-${idx}`}>
                                            &mdash; {contract.name}
                                        </Paragraph>
                                    ))
                                    : null}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }} margin={{ bottom: "19px" }}>
                                <Flex justify="space-between" align="center">
                                    <Title h6 capitalize>package prerequisites</Title>
                                    {isPackageWrite &&<TextButton onClick={() => !loading && editInfo("prereq", { key: data.key, prereq: data.prereq, sysarch: data.sysarch })}>
                                        <span><EditDetailsIcon /></span>
                                        <span>Edit</span>
                                    </TextButton>}
                                </Flex>
                                {Array.isArray(data.prereq)
                                    ? data.prereq.map((pr) => (
                                        <Link key={pr.packageNumber} to={`/packages/${toLower(pr.packageNumber)}`}>
                                            &mdash; {pr.name}
                                        </Link>
                                    ))
                                    : null}
                            </Box>
                            <Box flatten background="#F0F4FB" gap={{ bottom: 8 }}>
                                <Title h6 capitalize>required for</Title>
                                {Array.isArray(data.requiredFor)
                                    ? data.requiredFor.map((pr) => (
                                        <Link key={pr.packageNumber} to={`/packages/${toLower(pr.packageNumber)}`}>
                                            &mdash; {pr.name}
                                        </Link>
                                    ))
                                    : null}
                            </Box>
                        </Flex>
                    </Scrollbar>
                </Flex>
            </Flex>
            <Flex justify="space-between">
                <Flex gap={{ right: 12 }}>
                    <Button onClick={() => editInfo("packages", data)} disabled={loading || !isPackageWrite || size(uploadProgress)}>
                        <EditIcon />
                        Edit Info
                    </Button>
                    <Button onClick={() => verifyPackage()} disabled={loading || !isPackageWrite || size(uploadProgress)}>
                        {data.verified == 1 ? <RevokeIcon /> : <DoneIcon />}
                        {data.verified == 1 ? "Undo Verify" : "Verify"}
                    </Button>
                    <Button onClick={() => data.files.map(file => downloadPackage({ id: file.key, minos: data.minos }))} disabled={loading || size(uploadProgress)}>
                        <DownloadIcon />
                        Download
                    </Button>
                    <Button type="danger" onClick={() => removePackage()} disabled={loading || !isPackageWrite || (find(uploadProgress, { status: 3 }))}>
                        <RemoveIcon small />
                        Delete
                    </Button>
                </Flex>
            </Flex>
        </Content>
    );
};

export { PkgDetails };